import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, useSettingsStore } from './lib/store';
import FloatingMenu from './components/FloatingMenu';

// Pages
import HomePage from './pages/HomePage';
import AccessCodePage from './pages/AccessCodePage';
import CreatePinPage from './pages/CreatePinPage';
import VerifyPinPage from './pages/VerifyPinPage';
import LockScreenPage from './pages/LockScreenPage';
import DreamDefinePage from './pages/DreamDefinePage';
import DreamViewerPage from './pages/DreamViewerPage';
import DreamsLibraryPage from './pages/DreamsLibraryPage';
import SettingsPage from './pages/SettingsPage';
import ParamsPage from './pages/ParamsPage';
import InstallPromptPage from './pages/InstallPromptPage';
import CreateDreamPage from './pages/CreateDreamPage';
import PhotosPage from './pages/PhotosPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLocked } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/access" replace />;
  if (isLocked) return <Navigate to="/lock" replace />;
  return <>{children}</>;
}

// Pages where the floating menu should NOT show
const hideMenuPaths = ['/access', '/create-pin', '/verify-pin', '/lock', '/install', '/create'];

function useTheme() {
  const { themePreference } = useSettingsStore();

  useEffect(() => {
    const applyTheme = (mode: 'light' | 'dark') => {
      if (mode === 'light') {
        document.body.classList.add('light-mode');
      } else {
        document.body.classList.remove('light-mode');
      }
    };

    if (themePreference === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: light)');
      applyTheme(mq.matches ? 'light' : 'dark');
      const handler = (e: MediaQueryListEvent) => applyTheme(e.matches ? 'light' : 'dark');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } else {
      applyTheme(themePreference);
    }
  }, [themePreference]);
}

function AppContent() {
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();
  useTheme();

  const showMenu = isAuthenticated && !hideMenuPaths.includes(location.pathname);

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/access" element={<AccessCodePage />} />
        <Route path="/create-pin" element={<CreatePinPage />} />
        <Route path="/verify-pin" element={<VerifyPinPage />} />
        <Route path="/lock" element={<LockScreenPage />} />
        <Route path="/install" element={<InstallPromptPage />} />
        <Route path="/create" element={<CreateDreamPage />} />

        {/* Protected routes */}
        <Route path="/dream/define" element={<ProtectedRoute><DreamDefinePage /></ProtectedRoute>} />
        <Route path="/dream/:id" element={<ProtectedRoute><DreamViewerPage /></ProtectedRoute>} />
        <Route path="/dreams" element={<ProtectedRoute><DreamsLibraryPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/params" element={<ProtectedRoute><ParamsPage /></ProtectedRoute>} />
        <Route path="/photos" element={<ProtectedRoute><PhotosPage /></ProtectedRoute>} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showMenu && <FloatingMenu />}
    </>
  );
}

export default function App() {
  return (
    <div className="min-h-screen">
      <AppContent />
    </div>
  );
}
